import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Users, UserPlus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const FILTERS = [["", "All"], ["active", "Active"], ["attention", "Needs Attention"], ["completed", "Completed"], ["closed", "Closed"]];

const STATUS_TONE = {
  "Submitted": "bg-slate-100 text-slate-600", "Under Review": "bg-sky-100 text-sky-700",
  "In Process": "bg-indigo-100 text-indigo-700", "Interviewing": "bg-violet-100 text-violet-700",
  "Approved / Progressing": "bg-amber-100 text-amber-700", "Program / Reserve": "bg-teal-100 text-teal-700",
  "Career / Completed": "bg-emerald-100 text-emerald-700", "Closed": "bg-rose-100 text-rose-700",
};

export default function MyReferralsList({ meet = false }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "";
  const [items, setItems] = useState(null);

  useEffect(() => {
    setItems(null);
    api.get("/referral-partner/referrals", { params: filter ? { filter } : {} })
      .then((r) => setItems(r.data.items || [])).catch((e) => { toast.error(apiError(e)); setItems([]); });
  }, [filter]);

  const setFilter = (f) => setParams(f ? { filter: f } : {}, { replace: true });

  return (
    <div data-testid="my-referrals-page">
      <PageHeader title="My Candidates & Leads" description="Everyone you've referred and how they're progressing."
        actions={<Button className="bg-brand hover:bg-brand-strong text-white" onClick={() => navigate("/referrals/add")} data-testid="my-referrals-add"><UserPlus className="w-4 h-4 mr-1.5" />Add Lead</Button>} />
      {meet && <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800" data-testid="my-referrals-meet-hint">Open a candidate to schedule or document a meeting, call or interview.</div>}

      <div className="flex items-center gap-1 flex-wrap mb-4" data-testid="my-referrals-filters">
        {FILTERS.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`my-referrals-filter-${k || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === k ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>

      {items === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Users} title="No referrals here yet"
          description="Referrals you submit will appear here with their live status."
          action={<Button variant="outline" onClick={() => navigate("/referrals/add")} data-testid="my-referrals-empty-add"><UserPlus className="w-4 h-4 mr-1.5" />Add a Lead</Button>} testId="my-referrals-empty" />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Latest Milestone</th><th className="px-4 py-3">Last Update</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.person_id} onClick={() => navigate(`/referrals/${r.person_id}`)} data-testid={`my-referral-row-${r.person_id}`}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-1.5">{r.name}{r.needs_attention && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(r.submitted_at)}</td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[r.public_status] || "bg-slate-100 text-slate-600"}`}>{r.public_status}</span></td>
                    <td className="px-4 py-3 text-slate-600">{r.latest_milestone}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(r.last_update)}</td>
                    <td className="px-4 py-3 text-right"><span className="text-xs text-brand font-medium">View</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {items.map((r) => (
              <button key={r.person_id} onClick={() => navigate(`/referrals/${r.person_id}`)} data-testid={`my-referral-card-${r.person_id}`}
                className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800 flex items-center gap-1.5">{r.name}{r.needs_attention && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}</span>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[r.public_status] || "bg-slate-100 text-slate-600"}`}>{r.public_status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{r.latest_milestone} · Submitted {fmtDate(r.submitted_at)}</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
